import { useCallback, useMemo, useState } from "react"

import { ContentObjectItem, ContentObjectType } from "@vertesia/common"
import { Upload } from "lucide-react"
import { toast } from "sonner"

import { ActionTooltipButton } from "@components/buttons/ActionTooltipButton"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@ui/dialog"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@ui/select"

const NONE = "NONE"

interface UploadDocumentButtonProps {
    onUploadDone?: (document: ContentObjectItem) => void
    onMultipleUpload?: (documents: ContentObjectItem[]) => void
}
export function UploadDocumentButton({ onUploadDone, onMultipleUpload }: Readonly<UploadDocumentButtonProps>) {
    const { client, typeRegistry } = useUserSession()

    const [files, setFiles] = useState<File[]>([])
    const [isDragging, setIsDragging] = useState(false)
    const [uploadType, setUploadType] = useState<string>(NONE)
    const [open, setOpen] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [filterValue, setFilterValue] = useState("")

    const types = useMemo(() => {
        return typeRegistry?.types || ([] as ContentObjectType[])
    }, [typeRegistry?.types])

    const filteredOptions = types.filter((type) => type.name.toLowerCase().includes(filterValue.toLowerCase()))

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        setFilterValue(e.target.value)
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
    }

    const handleOpen = () => {
        setOpen(true)
        setIsUploading(false)
    }

    const handleClose = () => {
        setOpen(false)
        setIsUploading(false)
        setFiles([])
        setUploadType(NONE)
    }

    const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(true)
    }, [])

    const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)
    }, [])

    const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)

        const droppedFiles = Array.from(e.dataTransfer.files)
        setFiles((prev) => [...prev, ...droppedFiles])
    }, [])

    const handleUploadOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const selectedFiles = Array.from(event.target.files)
            setFiles((prev) => [...prev, ...selectedFiles])
        }
    }

    const onUpload = async () => {
        if (!client || !files?.length) {
            return
        }

        setIsUploading(true)
        try {
            const results = await Promise.all(Array.from(files).map((file) => client.objects.create({ type: uploadType !== NONE ? uploadType : undefined, content: file })))
            toast.success("Upload Successful", {
                description: "Files uploaded successfully",
                duration: 4000,
            })
            if (onUploadDone) {
                onUploadDone(results[0])
            }
            if (onMultipleUpload) {
                onMultipleUpload(results)
            }
        } catch (error) {
            toast.error("Upload Failed", {
                description: error instanceof Error ? error.message : "An unknown error occurred",
                duration: 4000,
            })
        }
        handleClose()
    }

    return (
        <>
            <ActionTooltipButton variant="outline" size="icon" icon={<Upload />} tooltip="Upload Document" onClick={handleOpen} className="size-9" />
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Upload Content</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Label>Number of files: {files ? files.length : 0}</Label>
                        <div onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center transition-colors duration-200 ease-in-out ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`}>
                            <p className="text-sm text-gray-500">
                                <Button variant="link" className="text-primary-dark underline hover:opacity-60 pr-1 pl-1" onClick={() => document.getElementById("file-upload")?.click()}>
                                    Upload file
                                </Button>
                                or drag and drop
                            </p>
                            <input id="file-upload" type="file" multiple={!!onMultipleUpload} className="hidden" onChange={handleUploadOnChange} />
                        </div>
                    </div>
                    <div>
                        <Label>Upload Type</Label>
                        <Select onValueChange={setUploadType} defaultValue={NONE}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select an Upload Type" />
                            </SelectTrigger>
                            <SelectContent>
                                {types && types.length > 5 && (
                                    <div className="p-2">
                                        <Input placeholder="Search Types..." value={filterValue} onChange={handleInputChange} onKeyDown={handleInputKeyDown} onClick={(e) => e.stopPropagation()} />
                                    </div>
                                )}
                                <SelectGroup className="max-h-[200px] overflow-y-auto">
                                    <SelectItem value={NONE}>Let Vertesia Choose</SelectItem>
                                    {filteredOptions.map((type) => (
                                        <SelectItem key={type.id} value={type.id}>
                                            {type.name}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant="destructive" onClick={handleClose} disabled={isUploading}>
                            Cancel
                        </Button>
                        <Button variant="outline" onClick={onUpload} disabled={!files || isUploading}>
                            {isUploading ? "Uploading..." : "Upload"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
